<template>
  <EntitySearchPopover
    :disabled="disabled"
    :items="teams"
    :loading="isLoading"
    :matchingItems="lastFetchedTeams"
    :open="openMenu"
    :resultsHeaderText="$t('common.team_other')"
    :searchScope="searchScope"
    :selectedItems="selectedTeams"
    :showAdvancedOption="false"
    @clearAll="handleClearAll"
    @openChange="openMenu = $event"
    @remove="handleRemove"
    @search="fetchTeams"
    @searchScopeChange="handleSearchScopeChange"
    @select="handleSelectSingleTeam"
  >
    <template v-slot:default>
      <slot name="activator" :selectedTeams="selectedTeams" :teams="teams" />
    </template>

    <template v-slot:item="{ item, searchTerm }">
      <TeamItem
        class="px-3 py-0"
        :team="item"
        :searchTerm="searchTerm"
        @click="handleSelectSingleTeam(item)"
      />
    </template>

    <template v-slot:selectedItem="{ item }">
      <TeamItem class="px-3 py-0" :team="item" />
    </template>
  </EntitySearchPopover>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import constants from '@/constants'
import mixin from '@/mixins/mixin.vue'
import getEntityId from '@/lib/getEntityId'

import EntitySearchPopover from '@/components/EntitySearchPopover.vue'
import TeamItem from './TeamItem.vue'

export default {
  name: 'TeamSelector',

  mixins: [mixin],

  components: {
    EntitySearchPopover,
    TeamItem,
  },

  props: {
    disabled: Boolean,
    selectedTeamIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      openMenu: false,
      isLoading: false,
      lastFetchedTeams: [],
      cachedTeams: {},
      searchTerms: '',
      searchScope: constants.SEARCH_SCOPES.site,
    }
  },

  async mounted() {
    await this.fetchTeams()
    await this.fetchSelectedTeam()
  },

  computed: {
    ...mapGetters(['organizationId', 'siteId']),

    teams() {
      return _.chain([
        ...(this.selectedTeamIds ?? []).map(id => this.cachedTeams[id]),
        ...this.lastFetchedTeams,
      ])
        .uniqBy('id')
        .compact()
        .value()
    },

    selectedTeams() {
      return this.teams.filter(team =>
        (this.selectedTeamIds ?? []).includes(team?.id)
      )
    },
  },

  methods: {
    buildTeamsCache(teamsToAdd) {
      this.cachedTeams = {
        ...this.cachedTeams,
        ..._.keyBy(teamsToAdd, 'id'),
      }
    },

    async fetchTeams(terms) {
      try {
        this.isLoading = true
        this.searchTerms = terms

        const params = {
          limit: 25,
          terms: terms,
          filters: {},
          sort: {
            by: terms ? '_score' : 'name',
            order: terms ? 'desc' : 'asc',
          },
        }

        if (this.searchScope === constants.SEARCH_SCOPES.site) {
          params.filters.site = this.siteId
        }

        const response = await api.teams.getTeamsStream(
          this.organizationId,
          params
        )

        this.lastFetchedTeams =
          response?.data?.results?.map?.(item => item.team) ?? []

        this.buildTeamsCache(this.lastFetchedTeams)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async fetchSelectedTeam() {
      const selectedTeamId = this.selectedTeamIds?.[0]
      if (!selectedTeamId) return

      try {
        this.isLoading = true

        const { data: selectedTeam } = await api.teams.getTeamById(
          this.organizationId,
          selectedTeamId
        )

        this.buildTeamsCache([selectedTeam])
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    // Always emitting an array to prepare this component to later support multiple selection
    handleSelectSingleTeam(team) {
      this.$emit('select', [getEntityId(team)])
    },

    handleRemove(team) {
      const remaining = this.selectedTeams.filter(t => t.id !== team.id)
      this.$emit('select', remaining.map(getEntityId))
    },

    handleClearAll() {
      this.$emit('select', [])
    },

    handleSearchScopeChange(scope) {
      this.searchScope = scope
      this.fetchTeams(this.searchTerms)
    },
  },
}
</script>
